/**
 *
 * EnvAuthenticatorStepOne Component
 *
 */

import {
  Button,
  Skeleton,
  stylize,
} from "@envestnet/envreact-component-library";
import classnames from "classnames";
import React, { useState } from "react";
import { EnvAuthenticatorStatusEnum } from "scenes/EnvAuthenticator/enums";
import { mfaFlow } from "utils/enum";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
  status: EnvAuthenticatorStatusEnum;
}

const defaultProps: DefaultProps = {
  status: EnvAuthenticatorStatusEnum.INITIAL,
};

export interface EnvAuthenticatorStepOneProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;

  onNext: (obj?: { authenticatorType: any }) => void;
  onCancel: () => void;
  onCantScan: () => void;
  loadQrCode: (obj: {
    authenticatorType: string;
    currentLayout: mfaFlow;
  }) => void;
  qrCodeImg: string;
  currentLayout: mfaFlow;
}

type Props = InjectedProps & EnvAuthenticatorStepOneProps;

const EnvAuthenticatorStepOne = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onNext,
    onCancel,
    onCantScan,
    loadQrCode,
    qrCodeImg,
    currentLayout,
    status,
  } = props;

  const [qrRendered, setQrRendered] = useState<boolean>(false);

  React.useEffect(() => {
    if (!qrCodeImg) {
      loadQrCode({
        authenticatorType: "envapp",
        currentLayout,
      });
    }
  }, [currentLayout]);

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div>
        <ol className={classnames(classes.orderedListClass)}>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem1)}
          </li>
          <li className="header-text-font-family">
            {formatMessage(messages.listItem2)}
          </li>
        </ol>

        <img
          src={qrCodeImg}
          alt={formatMessage(messages.qrCode)}
          loading="lazy"
          onLoad={() => {
            setQrRendered(true);
          }}
          style={
            qrRendered ? { visibility: "visible" } : { visibility: "hidden" }
          }
        ></img>
        {!qrRendered && (
          <Skeleton
            height={119}
            width={122}
            variant="rect"
            style={{ margin: "auto", marginBottom: "16px" }}
          />
        )}

        <div className={classes.warningContainer}>
          <span
            className={classnames(
              classes.warningText,
              "header-text-font-family"
            )}
          >
            {formatMessage(messages.warning)}
          </span>
        </div>

        <div>
          <Button
            buttonStyle="link"
            buttonSize="regular"
            className={classnames(
              classes.linkButtonClass,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cantScanLabel)}
            onClick={() => onCantScan()}
            disabled={!qrRendered}
            aria-label="cant scan button"
          />
        </div>
        <div>
          <Button
            buttonStyle="primary"
            buttonSize="regular"
            className={classnames(
              classes.nextButtonClass,
              "no-border-radius",
              "header-text-font-family"
            )}
            name={formatMessage(messages.nextButtonLabel)}
            disabled={!qrRendered}
            loading={
              status === EnvAuthenticatorStatusEnum.PROCESSING || !qrRendered
            }
            onClick={() => onNext()}
            aria-label="next button"
          />
        </div>

        <div>
          <Button
            buttonStyle="link"
            buttonSize="regular"
            className={classnames(
              classes.linkButtonClass,
              "header-text-font-family"
            )}
            name={formatMessage(messages.cancelButtonLabel)}
            onClick={() => onCancel()}
            disabled={!qrRendered}
            aria-label="cancel button"
          />
        </div>
      </div>
    </div>
  );
};

EnvAuthenticatorStepOne.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(EnvAuthenticatorStepOne)
);
